import React, { useState, useEffect } from 'react';
import { array, bool, func, number, object, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import classNames from 'classnames';
import {
  ensureListing,
  findApplicableDiscount,
  getDayBasedPricing,
  getDateBasedPricing,
} from '../../../util/data';
import { FormattedMessage, intlShape, injectIntl } from '../../../util/reactIntl';
import {
  required,
  bookingDatesRequired,
  composeValidators,
  numberAtLeast,
  validateAddonsQuantity,
} from '../../../util/validators';
import {
  START_DATE,
  END_DATE,
  getStartOf,
  addTime,
  isSameDay,
  isDateSameOrAfter,
  isInRange,
  timeOfDayFromLocalToTimeZone,
  timeOfDayFromTimeZoneToLocal,
  monthIdString,
  initialVisibleMonth,
  daysBetween,
} from '../../../util/dates';
import { LINE_ITEM_DAY, LINE_ITEM_NIGHT, TIME_SLOT_TIME, propTypes } from '../../../util/types';
import { BOOKING_PROCESS_NAME } from '../../../transactions/transaction';
import {
  Form,
  IconArrowHead,
  PrimaryButton,
  FieldDateRangeInput,
  H6,
  FieldTextInput,
  FieldSelect,
  BlueButton,
} from '../../../components';
import EstimatedCustomerBreakdownMaybe from '../EstimatedCustomerBreakdownMaybe';
import Addons, { AddonsWithQuantity } from './Addons';
import arrayMutators from 'final-form-arrays';
import css from './BookingDatesForm.module.css';

const TODAY = new Date();
const MIN_SEATS_NUMBER = 1;

const nextMonthFn = (currentMoment, timeZone, offset = 1) =>
  getStartOf(currentMoment, 'month', timeZone, offset, 'months');
const prevMonthFn = (currentMoment, timeZone, offset = 1) =>
  getStartOf(currentMoment, 'month', timeZone, -1 * offset, 'months');
const endOfRange = (date, dayCountAvailableForBooking, timeZone) =>
  getStartOf(date, 'day', timeZone, dayCountAvailableForBooking - 1, 'days');

const timeSlotsContain = (timeSlots, date, timeZone) => {
  const foundIndex = timeSlots.findIndex(slot =>
    isInRange(date, slot.attributes.start, slot.attributes.end, 'hour', timeZone)
  );
  return foundIndex > -1;
};

const pickMonthlyTimeSlots = (monthlyTimeSlots, date, timeZone) => {
  const monthId = monthIdString(date, timeZone);
  return monthlyTimeSlots?.[monthId]?.timeSlots || [];
};

const firstBlockedBetween = (monthlyTimeSlots, startDate, endDate, timeZone) => {
  const firstDate = getStartOf(startDate, 'day', timeZone, 1, 'days');
  if (isSameDay(firstDate, endDate, timeZone)) {
    return null;
  }
  const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, firstDate, timeZone);
  const contains = timeSlotsContain(timeSlots, firstDate, timeZone);
  return contains ? firstBlockedBetween(monthlyTimeSlots, firstDate, endDate, timeZone) : firstDate;
};

const lastBlockedBetween = (monthlyTimeSlots, startDate, endDate, timeZone) => {
  const previousDate = getStartOf(endDate, 'day', timeZone, -1, 'days');
  if (isSameDay(previousDate, startDate, timeZone)) {
    return null;
  }
  const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, previousDate, timeZone);
  return timeSlotsContain(timeSlots, previousDate, timeZone)
    ? lastBlockedBetween(monthlyTimeSlots, startDate, previousDate, timeZone)
    : previousDate;
};

const isBlockedBetween = (monthlyTimeSlots, timeZone) => (startDate, endDate) => {
  const startInListingTZ = getStartOf(
    timeOfDayFromLocalToTimeZone(startDate, timeZone),
    'day',
    timeZone
  );
  const endInListingTZ = getStartOf(
    timeOfDayFromLocalToTimeZone(endDate, timeZone),
    'day',
    timeZone
  );
  return !!firstBlockedBetween(monthlyTimeSlots, startInListingTZ, endInListingTZ, timeZone);
};

const isStartDateSelected = (timeSlots, startDate, endDate, focusedInput) => {
  return (
    timeSlots && startDate && (!endDate || focusedInput === END_DATE) && focusedInput !== START_DATE
  );
};

const endDateToPickerDate = (unitType, endDate, timeZone) => {
  const isValid = endDate instanceof Date;
  const isDaily = unitType === LINE_ITEM_DAY;
  if (!isValid) {
    return null;
  } else if (isDaily) {
    return getStartOf(endDate, 'day', timeZone, -1, 'days');
  } else {
    return endDate;
  }
};

const isOutsideRangeFn = (
  monthlyTimeSlots,
  startDate,
  endDate,
  lineItemUnitType,
  dayCountAvailableForBooking,
  timeZone
) => focusedInput => {
  const endOfAvailableRange = dayCountAvailableForBooking - 1;
  const endOfAvailableRangeDate = getStartOf(TODAY, 'day', timeZone, endOfAvailableRange, 'days');
  const outOfBookableDate = getStartOf(TODAY, 'day', timeZone, endOfAvailableRange + 1, 'days');
  const startOfStartDay = getStartOf(startDate, 'day', timeZone);
  const startDateSelected = isStartDateSelected(monthlyTimeSlots, startDate, endDate, focusedInput);
  const endOfBookableRange = startDateSelected
    ? firstBlockedBetween(monthlyTimeSlots, startDate, outOfBookableDate, timeZone)
    : null;
  if (endOfBookableRange) {
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const lastDayToEndBooking = endDateToPickerDate(
        lineItemUnitType,
        endOfBookableRange,
        timeZone
      );
      return (
        !isDateSameOrAfter(dayInListingTZ, startOfStartDay) ||
        !isDateSameOrAfter(lastDayToEndBooking, dayInListingTZ)
      );
    };
  }
  const endDateSelected = monthlyTimeSlots && endDate && !startDate && focusedInput !== END_DATE;
  const previousBookedDate = endDateSelected
    ? lastBlockedBetween(monthlyTimeSlots, TODAY, endDate, timeZone)
    : null;
  if (previousBookedDate) {
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const firstDayToStartBooking = getStartOf(previousBookedDate, 'day', timeZone, 1, 'days');
      return (
        !isDateSameOrAfter(dayInListingTZ, firstDayToStartBooking) ||
        !isDateSameOrAfter(endOfAvailableRangeDate, dayInListingTZ)
      );
    };
  }
  return day => {
    const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
    const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
    return (
      !isDateSameOrAfter(dayInListingTZ, TODAY) ||
      !isDateSameOrAfter(endOfAvailableRangeDate, dayInListingTZ)
    );
  };
};

const timeSlotEqualsDay = (timeSlot, day, timeZone) => {
  const isTimeBased = timeSlot?.attributes?.type === TIME_SLOT_TIME;
  return isTimeBased
    ? isInRange(day, timeSlot.attributes.start, timeSlot.attributes.end, undefined, timeZone)
    : false;
};

const isDayBlockedFn = (
  monthlyTimeSlots,
  startDate,
  endDate,
  unitType,
  dayCountAvailableForBooking,
  timeZone
) => focusedInput => {
  const endOfAvailableRange = dayCountAvailableForBooking - 1;
  const outOfBookableDate = getStartOf(TODAY, 'day', timeZone, endOfAvailableRange + 1, 'days');
  const timeSlotsOnSelectedMonth = pickMonthlyTimeSlots(monthlyTimeSlots, startDate, timeZone);
  const startDateSelected = isStartDateSelected(
    timeSlotsOnSelectedMonth,
    startDate,
    endDate,
    focusedInput
  );
  const endOfBookableRange = startDateSelected
    ? firstBlockedBetween(monthlyTimeSlots, startDate, outOfBookableDate, timeZone)
    : null;
  const selectingEndDate =
    timeSlotsOnSelectedMonth &&
    !startDate &&
    !endDate &&
    focusedInput === END_DATE &&
    unitType === LINE_ITEM_NIGHT;
  if (selectingEndDate) {
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, dayInListingTZ, timeZone);
      return !timeSlots.find(timeSlot => timeSlotEqualsDay(timeSlot, dayInListingTZ));
    };
  } else if (endOfBookableRange || !timeSlotsOnSelectedMonth) {
    return () => false;
  } else {
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, dayInListingTZ, timeZone);
      return !timeSlots.find(timeSlot => timeSlotEqualsDay(timeSlot, dayInListingTZ));
    };
  }
};

const fetchMonthData = (
  date,
  listingId,
  dayCountAvailableForBooking,
  timeZone,
  onFetchTimeSlots
) => {
  const endOfRangeDate = endOfRange(TODAY, dayCountAvailableForBooking, timeZone);
  if (isInRange(date, TODAY, endOfRangeDate)) {
    const start = isDateSameOrAfter(TODAY, date) ? TODAY : date;
    const nextMonthDate = nextMonthFn(date, timeZone);
    const end = isDateSameOrAfter(nextMonthDate, endOfRangeDate)
      ? getStartOf(endOfRangeDate, 'day', timeZone)
      : nextMonthDate;
    onFetchTimeSlots(listingId, start, end, timeZone);
  }
};

const handleMonthClick = (
  currentMonth,
  monthlyTimeSlots,
  fetchMonthData,
  dayCountAvailableForBooking,
  timeZone,
  listingId,
  onFetchTimeSlots
) => monthFn => {
  fetchMonthData(
    monthFn(currentMonth, timeZone, 2),
    listingId,
    dayCountAvailableForBooking,
    timeZone,
    onFetchTimeSlots
  );
  const monthId = monthIdString(currentMonth, timeZone);
  const currentMonthData = monthlyTimeSlots[monthId];
  if (currentMonthData && currentMonthData.fetchTimeSlotsError) {
    fetchMonthData(
      currentMonth,
      listingId,
      dayCountAvailableForBooking,
      timeZone,
      onFetchTimeSlots
    );
  }
};

const handleFormSubmit = (setFocusedInput, onSubmit) => e => {
  const { startDate, endDate } = e.bookingDates || {};
  if (!startDate) {
    e.preventDefault();
    setFocusedInput(START_DATE);
  } else if (!endDate) {
    e.preventDefault();
    setFocusedInput(END_DATE);
  } else {
    onSubmit(e);
  }
};

const handleFocusedInputChange = setFocusedInput => focusedInput => {
  setFocusedInput(focusedInput);
};

const handleFormSpyChange = (
  listingId,
  isOwnListing,
  fetchLineItemsInProgress,
  onFetchTransactionLineItems,
  addonsFromPublicData,
  discountsFromPublicData,
  dayPricingsFromPublicData,
  datePricingsFromPublicData,
  price,
  config,
  charterBoat
) => formValues => {
  const bookingDates =
    formValues.values && formValues.values.bookingDates ? formValues.values.bookingDates : {};
  const { startDate, endDate } = bookingDates;
  const formAddons = formValues?.values?.addons;
  const addons = formAddons
    ? formAddons?.map(addonKey => {
        const addonConfig = addonsFromPublicData.find(a => a.key === addonKey);
        const addonQuantity = formValues?.values?.addonsQuantity?.[addonKey] || 1;
        return {
          ...addonConfig,
          quantity: addonQuantity,
        };
      })
    : [];
  const addonsMaybe = addons ? { addons } : {};
  const datesDayDifference = daysBetween(startDate, endDate);
  const durationDiscount = findApplicableDiscount(discountsFromPublicData, datesDayDifference);
  const durationDiscountMaybe = durationDiscount ? { durationDiscount } : {};
  const seats = formValues?.values?.seats ? Number(formValues.values.seats) : null;
  const seatsMaybe = seats ? { seats } : {};
  const hasBookingDates = charterBoat ? startDate && endDate && seats : startDate && endDate;
  const shouldFetchDayBasedPricing = hasBookingDates && dayPricingsFromPublicData;
  const dayBasedPricing =
    shouldFetchDayBasedPricing &&
    getDayBasedPricing(bookingDates, dayPricingsFromPublicData, price, config);
  const dayBasedPricingMaybe = dayBasedPricing ? { dayBasedPricing } : {};
  const shouldFetchDateBasedPricing = hasBookingDates && datePricingsFromPublicData;
  const dateBasedPricing =
    shouldFetchDateBasedPricing &&
    getDateBasedPricing(bookingDates, datePricingsFromPublicData, price, config);
  const dateBasedPricingMaybe = dateBasedPricing ? { dateBasedPricing } : {};
  if (startDate && endDate && !fetchLineItemsInProgress) {
    onFetchTransactionLineItems({
      orderData: {
        ...addonsMaybe,
        ...dayBasedPricingMaybe,
        ...dateBasedPricingMaybe,
        ...durationDiscountMaybe,
        ...seatsMaybe,
        bookingStart: startDate,
        bookingEnd: endDate,
        discountCode: formValues?.values?.discountCode,
      },
      listingId,
      isOwnListing,
    });
  }
};

const PrevIcon = props => (
  <IconArrowHead {...props} direction="left" rootClassName={css.arrowIcon} />
);
const NextIcon = props => (
  <IconArrowHead {...props} direction="right" rootClassName={css.arrowIcon} />
);

const Next = props => {
  const { currentMonth, dayCountAvailableForBooking, timeZone } = props;
  const nextMonthDate = nextMonthFn(currentMonth, timeZone);
  return isDateSameOrAfter(
    nextMonthDate,
    endOfRange(TODAY, dayCountAvailableForBooking, timeZone)
  ) ? null : (
    <NextIcon />
  );
};
const Prev = props => {
  const { currentMonth, timeZone } = props;
  const prevMonthDate = prevMonthFn(currentMonth, timeZone);
  const currentMonthDate = getStartOf(TODAY, 'month', timeZone);
  return isDateSameOrAfter(prevMonthDate, currentMonthDate) ? <PrevIcon /> : null;
};

export const BookingDatesFormComponent = props => {
  const [focusedInput, setFocusedInput] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(getStartOf(TODAY, 'month', props.timeZone));
  const [discountCode, setDiscountCode] = useState('');
  // Local state to toggle the reserve section and confirm reservation
  const [reserveMode, setReserveMode] = useState(false);
  const [reservationConfirmed, setReservationConfirmed] = useState(false);

  useEffect(() => {
    if (props.onMonthChanged) {
      props.onMonthChanged(monthId);
    }
  }, [currentMonth]);

  const {
    rootClassName,
    className,
    price: unitPrice,
    listingId,
    isOwnListing,
    fetchLineItemsInProgress,
    onFetchTransactionLineItems,
    onSubmit,
    timeZone,
    dayCountAvailableForBooking,
    marketplaceName,
    payoutDetailsWarning,
    publicData,
    addonsFromPublicData,
    discountsFromPublicData,
    dayPricingsFromPublicData,
    datePricingsFromPublicData,
    charterBoat,
    charterBoatSeats,
    config,
    ...rest
  } = props;

  const codes = Array.isArray(props.codes)
    ? [
        ...props.codes,
        { code: 'Ottawa10', percentage: '10' },
        { code: 'Paradise10', percentage: '10' },
        { code: 'Ny1sKjvp', percentage: '20' },
      ]
    : [
        { code: 'Ottawa10', percentage: '10' },
        { code: 'Paradise10', percentage: '10' },
        { code: 'Ny1sKjvp', percentage: '20' },
      ];
  const rentalLocation = props.rentalLocation;
  const classes = classNames(rootClassName || css.root, className);
  const onFormSubmit = handleFormSubmit(setFocusedInput, onSubmit);
  const onFocusedInputChange = handleFocusedInputChange(setFocusedInput);
  let selectedCode = '';

  const onFormSpyChange = handleFormSpyChange(
    listingId,
    isOwnListing,
    fetchLineItemsInProgress,
    onFetchTransactionLineItems,
    addonsFromPublicData,
    discountsFromPublicData,
    dayPricingsFromPublicData,
    datePricingsFromPublicData,
    unitPrice,
    config,
    charterBoat
  );

  const validateCode = value => {
    const codeExists = codes.some(code => code.code === value);
    let selectedCode = '';
    if (codeExists) {
      const code = codes.find(code => code.code === value);
      selectedCode = code.percentage;
      setDiscountCode(value);
      console.log(selectedCode);
    }
  };

  // Simple required validator for the reserve email field.
  const requiredEmail = value => (value ? undefined : 'Email is required');

  const radius = { borderRadius: '30px' };

  return (
    <FinalForm
      {...rest}
      mutators={{ ...arrayMutators }}
      unitPrice={unitPrice}
      onSubmit={onFormSubmit}
      initialValues={{
        seats: charterBoatSeats ? MIN_SEATS_NUMBER : undefined,
        reserveEmail: '',
      }}
      render={fieldRenderProps => {
        const {
          endDatePlaceholder,
          startDatePlaceholder,
          formId,
          handleSubmit,
          intl,
          lineItemUnitType,
          values,
          monthlyTimeSlots,
          lineItems,
          fetchLineItemsError,
          onFetchTimeSlots,
          form,
        } = fieldRenderProps;
        const { startDate, endDate } =
          (values && values.bookingDates ? values.bookingDates : {}) || {};

        const startDateErrorMessage = intl.formatMessage({
          id: 'FieldDateRangeInput.invalidStartDate',
        });
        const endDateErrorMessage = intl.formatMessage({
          id: 'FieldDateRangeInput.invalidEndDate',
        });
        const breakdownData =
          startDate && endDate
            ? {
                startDate,
                endDate,
              }
            : null;
        const showEstimatedBreakdown =
          breakdownData && lineItems && !fetchLineItemsInProgress && !fetchLineItemsError;
        const dateFormatOptions = {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        };
        const startOfToday = getStartOf(TODAY, 'day', timeZone);
        const tomorrow = addTime(startOfToday, 1, 'days');
        const startDatePlaceholderText =
          startDatePlaceholder || intl.formatDate(startOfToday, dateFormatOptions);
        const endDatePlaceholderText =
          endDatePlaceholder || intl.formatDate(tomorrow, dateFormatOptions);

        const onMonthClick = handleMonthClick(
          currentMonth,
          monthlyTimeSlots,
          fetchMonthData,
          dayCountAvailableForBooking,
          timeZone,
          listingId,
          onFetchTimeSlots
        );
        const isDayBlocked = isDayBlockedFn(
          monthlyTimeSlots,
          startDate,
          endDate,
          lineItemUnitType,
          dayCountAvailableForBooking,
          timeZone
        );
        const isOutsideRange = isOutsideRangeFn(
          monthlyTimeSlots,
          startDate,
          endDate,
          lineItemUnitType,
          dayCountAvailableForBooking,
          timeZone
        );
        const showAddons = addonsFromPublicData && addonsFromPublicData.length > 0;
        const seats = charterBoatSeats || MIN_SEATS_NUMBER;
        const hasFormSeats = values.seats && typeof values.seats !== 'undefined';
        const formSeats = hasFormSeats ? values.seats : null;
        const showAddonsWithQuantity = charterBoat && formSeats > 0;
        const seatsMessage = intl.formatMessage({
          id: 'BookingDatesForm.seats',
        });
        const seatsPlaceholderMessage = intl.formatMessage(
          {
            id: 'BookingDatesForm.seatsPlaceholder',
          },
          { count: MIN_SEATS_NUMBER }
        );
        const seatsRequiredMessage = intl.formatMessage({
          id: 'BookingDatesForm.seatsRequired',
        });
        const charterBoatSeatsNumber = charterBoat ? Number(charterBoatSeats) : null;
        const hasCharterBoatSeats = charterBoatSeatsNumber > 0;
        const seatsArray =
          charterBoatSeatsNumber && hasCharterBoatSeats
            ? [...Array(Number(charterBoatSeatsNumber)).keys()].map(i => i + 1)
            : [];
        const seatsValidators = numberAtLeast(seatsRequiredMessage, 1);
        const addonsQuantityError = addonsFromPublicData?.some(addon => {
          const isChecked = values?.addons?.includes(addon.key);
          const isQuantityAboveMaximum =
            values?.addonsQuantity?.[addon.key] > formSeats ||
            values?.addonsQuantity?.[addon.key] > seats;
          const isQuantityBelowMinimum = values?.addonsQuantity?.[addon.key] < MIN_SEATS_NUMBER;
          return isChecked && (isQuantityAboveMaximum || isQuantityBelowMinimum);
        });

        // Define the reserve submission handler inside the render callback so that "values" is in scope.
        const handleReserveSubmitInternal = async () => {
          const reserveEmailValue = values.reserveEmail;
          if (!reserveEmailValue) {
            alert('Please enter your email.');
            return;
          }
          // Construct the payload with required fields.
          const payload = {
            customerId: 'WetRentals',
            email: reserveEmailValue,
            inventoryId: listingId,
            locationId: 'WetRentals',
          };
          try {
            const response = await fetch('https://v2.api.rentaltide.com/holdAsset', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
            if (!response.ok) {
              throw new Error('API call failed');
            }
            const data = await response.json();
            console.log('Waitlist entry created:', data);
            setReserveMode(false);
            setReservationConfirmed(true);
            form.change('reserveEmail', '');
          } catch (error) {
            console.error('Error reserving boat:', error);
            alert('There was an error processing your reservation. Please try again.');
          }
        };

        return (
          <Form onSubmit={handleSubmit} className={classes} enforcePagePreloadFor="CheckoutPage">
            <FormSpy subscription={{ values: true }} onChange={onFormSpyChange} />
            <FieldDateRangeInput
              className={css.bookingDates}
              name="bookingDates"
              isDaily={lineItemUnitType === LINE_ITEM_DAY}
              startDateId={`${formId}.bookingStartDate`}
              startDateLabel={intl.formatMessage({
                id: 'BookingDatesForm.bookingStartTitle',
              })}
              startDatePlaceholderText={startDatePlaceholderText}
              endDateId={`${formId}.bookingEndDate`}
              endDateLabel={intl.formatMessage({
                id: 'BookingDatesForm.bookingEndTitle',
              })}
              endDatePlaceholderText={endDatePlaceholderText}
              focusedInput={focusedInput}
              onFocusedInputChange={onFocusedInputChange}
              format={v => {
                const { startDate, endDate } = v || {};
                const formattedStart = startDate
                  ? timeOfDayFromTimeZoneToLocal(startDate, timeZone)
                  : startDate;
                const formattedEnd = endDate
                  ? timeOfDayFromTimeZoneToLocal(endDate, timeZone)
                  : endDate;
                return v ? { startDate: formattedStart, endDate: formattedEnd } : v;
              }}
              parse={v => {
                const { startDate, endDate } = v || {};
                const parsedStart = startDate
                  ? getStartOf(timeOfDayFromLocalToTimeZone(startDate, timeZone), 'day', timeZone)
                  : startDate;
                const parsedEnd = endDate
                  ? getStartOf(timeOfDayFromLocalToTimeZone(endDate, timeZone), 'day', timeZone)
                  : endDate;
                return v ? { startDate: parsedStart, endDate: parsedEnd } : v;
              }}
              useMobileMargins
              validate={composeValidators(
                required(
                  intl.formatMessage({
                    id: 'BookingDatesForm.requiredDate',
                  })
                ),
                bookingDatesRequired(startDateErrorMessage, endDateErrorMessage)
              )}
              initialVisibleMonth={initialVisibleMonth(startDate || startOfToday, timeZone)}
              navNext={
                <Next
                  currentMonth={currentMonth}
                  timeZone={timeZone}
                  dayCountAvailableForBooking={dayCountAvailableForBooking}
                />
              }
              navPrev={<Prev currentMonth={currentMonth} timeZone={timeZone} />}
              onPrevMonthClick={() => {
                setCurrentMonth(prevMonth => prevMonthFn(prevMonth, timeZone));
                onMonthClick(prevMonthFn);
              }}
              onNextMonthClick={() => {
                setCurrentMonth(prevMonth => nextMonthFn(prevMonth, timeZone));
                onMonthClick(nextMonthFn);
              }}
              isDayBlocked={isDayBlocked}
              isOutsideRange={isOutsideRange}
              isBlockedBetween={isBlockedBetween(monthlyTimeSlots, timeZone)}
              disabled={fetchLineItemsInProgress}
              onClose={event =>
                setCurrentMonth(getStartOf(event?.startDate ?? startOfToday, 'month', timeZone))
              }
            />
            {codes && (
              <FieldTextInput
                id="discount"
                name="discountCode"
                label="Discount Code"
                placeholder="Input discount code here"
                validate={validateCode}
                type="text"
                className={`${
                  codes?.some(code => code?.code === values?.discountCode)
                    ? css.validCode
                    : css.invalidCode
                } ${css.discountCode}`}
              />
            )}
            {rentalLocation && (
              <FieldTextInput
                id="rentalLocation"
                name="rentalLocation"
                label="Where Would You Like To Ride?"
                placeholder="Input desired rental location here"
                validate={validateCode}
                required={true}
                type="text"
                className={css.rentalLocation}
              />
            )}
            {charterBoat && (
              <FieldSelect
                id="seats"
                name="seats"
                className={css.seats}
                label={seatsMessage}
                disabled={!seatsArray.length}
                validate={seatsValidators}
              >
                {seatsArray.map(s => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </FieldSelect>
            )}
            {showAddons &&
              (showAddonsWithQuantity ? (
                <AddonsWithQuantity
                  addonsFromPublicData={addonsFromPublicData}
                  values={values}
                  formSeats={formSeats}
                  seats={seats}
                  form={form}
                  addonsQuantityError={addonsQuantityError}
                />
              ) : (
                <Addons addonsFromPublicData={addonsFromPublicData} />
              ))}
            {showEstimatedBreakdown ? (
              <div className={css.priceBreakdownContainer}>
                <H6 as="h3" className={css.bookingBreakdownTitle}>
                  <FormattedMessage id="BookingDatesForm.priceBreakdownTitle" />
                </H6>
                <hr className={css.totalDivider} />
                <EstimatedCustomerBreakdownMaybe
                  breakdownData={breakdownData}
                  lineItems={lineItems}
                  discountCode={values?.discountCode}
                  discountPercent={selectedCode}
                  rentalLocation={values?.rentalLocation}
                  timeZone={timeZone}
                  currency={unitPrice.currency}
                  marketplaceName={marketplaceName}
                  processName={BOOKING_PROCESS_NAME}
                  dayBasedPricing={dayPricingsFromPublicData}
                  dateBasedPricing={datePricingsFromPublicData}
                />
              </div>
            ) : null}
            {fetchLineItemsError ? (
              <span className={css.sideBarError}>
                <FormattedMessage id="BookingDatesForm.fetchLineItemsError" />
              </span>
            ) : null}

            <div className={css.submitButton}>
              <PrimaryButton
                type="submit"
                inProgress={fetchLineItemsInProgress}
                disabled={addonsQuantityError}
              >
                <FormattedMessage id="BookingDatesForm.requestToBook" />
              </PrimaryButton>
            </div>
            <p className={css.finePrint}>
              {payoutDetailsWarning ? (
                payoutDetailsWarning
              ) : (
                <FormattedMessage
                  id={
                    isOwnListing
                      ? 'BookingDatesForm.ownListing'
                      : 'BookingDatesForm.youWontBeChargedInfo'
                  }
                />
              )}
            </p>

            {/* Reserve Section moved below the booking button and fine print */}
            <div className={css.reserveContainer} style={{ margin: '20px 0', padding: '10px' }}>
              {reservationConfirmed ? (
                <div style={{ textAlign: 'center', fontWeight: 'bold' }}>
                  <FormattedMessage
                    id="BookingDatesForm.thanksForReserving"
                    defaultMessage="Thanks for reserving!"
                  />
                </div>
              ) : (
                <>
                  {!reserveMode ? (
                    <BlueButton type="button" onClick={() => setReserveMode(true)}>
                      Reserve With Only An Email
                    </BlueButton>
                  ) : (
                    <div style={{ marginTop: '10px' }}>
                      <FieldTextInput
                        id="reserveEmail"
                        name="reserveEmail"
                        label="Your Email"
                        placeholder="Enter your email"
                        type="email"
                        validate={requiredEmail}
                      />
                      <div style={{ marginTop: '10px' }}>
                        <BlueButton type="button" onClick={handleReserveSubmitInternal}>
                          Confirm Reservation
                        </BlueButton>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Form>
        );
      }}
    />
  );
};

BookingDatesFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  isOwnListing: false,
  startDatePlaceholder: null,
  endDatePlaceholder: null,
  lineItems: null,
  fetchLineItemsError: null,
  monthlyTimeSlots: null,
};

BookingDatesFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  marketplaceName: string.isRequired,
  lineItemUnitType: propTypes.lineItemUnitType.isRequired,
  price: propTypes.money,
  isOwnListing: bool,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  intl: intlShape.isRequired,
  startDatePlaceholder: string,
  endDatePlaceholder: string,
  dayCountAvailableForBooking: number.isRequired,
};

const BookingDatesForm = compose(injectIntl)(BookingDatesFormComponent);
BookingDatesForm.displayName = 'BookingDatesForm';

export default BookingDatesForm;
